import { MutableRefObject, RefObject, useCallback, useEffect, useRef } from "react";
import { PlayerVideoHandle } from "../components/playerVideoTypes";
import {
  beginSeekTransaction,
  evaluateSeekProgress,
  SEEK_RETRY_INTERVAL_MS,
  SeekTransaction,
} from "./seekTransaction";

type PlayerSeekCallbacks = {
  onPosition: (seconds: number) => void;
  onIntent: () => void;
  onTimeout: (targetSeconds: number) => void;
  onActivity: () => void;
};

type PlayerSeekContext = PlayerSeekCallbacks & {
  durationSeconds: number;
  decoderOffsetSeconds: number;
  positionRef: MutableRefObject<number>;
  videoRef: RefObject<PlayerVideoHandle>;
};

/** Owns optimistic seek intent, decoder retries, and clock acknowledgement. */
export function usePlayerSeek(context: PlayerSeekContext) {
  const contextRef = useRef(context);
  const pendingRef = useRef<SeekTransaction | null>(null);
  const lastReportedRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  contextRef.current = context;

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = undefined;
  }, []);

  const scheduleRetryCheck = useCallback(() => {
    clearRetryTimer();
    retryTimerRef.current = setTimeout(() => {
      const transaction = pendingRef.current;
      if (!transaction) return;
      const current = contextRef.current;
      const decision = evaluateSeekProgress(
        transaction,
        lastReportedRef.current,
        current.durationSeconds,
      );
      if (decision.kind === "accept") {
        pendingRef.current = null;
        return;
      }
      if (decision.kind === "timeout") {
        pendingRef.current = null;
        current.onPosition(decision.positionSeconds);
        if (transaction.notifyOnTimeout) current.onTimeout(transaction.targetSeconds);
        return;
      }
      if (decision.kind === "retry") {
        pendingRef.current = decision.transaction;
        current.videoRef.current?.seek(Math.max(0, decision.transaction.targetSeconds - current.decoderOffsetSeconds));
      }
      scheduleRetryCheck();
    }, SEEK_RETRY_INTERVAL_MS);
  }, [clearRetryTimer]);

  const seek = useCallback((requestedSeconds: number, reportIntent = true) => {
    const current = contextRef.current;
    const transaction = beginSeekTransaction(
      requestedSeconds,
      current.durationSeconds,
      Date.now(),
      reportIntent,
    );
    if (!transaction) return;
    lastReportedRef.current = current.positionRef.current;
    pendingRef.current = transaction;
    current.videoRef.current?.seek(Math.max(0, transaction.targetSeconds - current.decoderOffsetSeconds));
    if (reportIntent) {
      // Direct manipulation should feel immediate, but an automatic resume is
      // not proof that a remote decoder accepted the requested offset. Keep
      // the UI on its real media clock until progress acknowledges it.
      current.onPosition(transaction.targetSeconds);
      current.onActivity();
      current.onIntent();
    }
    scheduleRetryCheck();
  }, [scheduleRetryCheck]);

  const reconcile = useCallback((reportedSeconds: number): number | null => {
    lastReportedRef.current = reportedSeconds;
    const transaction = pendingRef.current;
    if (!transaction) return reportedSeconds;
    const current = contextRef.current;
    const decision = evaluateSeekProgress(
      transaction,
      reportedSeconds,
      current.durationSeconds,
    );
    if (decision.kind === "hold") return null;
    if (decision.kind === "retry") {
      pendingRef.current = decision.transaction;
      current.videoRef.current?.seek(Math.max(0, decision.transaction.targetSeconds - current.decoderOffsetSeconds));
      scheduleRetryCheck();
      return null;
    }
    pendingRef.current = null;
    clearRetryTimer();
    if (decision.kind === "timeout" && transaction.notifyOnTimeout) current.onTimeout(transaction.targetSeconds);
    return decision.positionSeconds;
  }, [clearRetryTimer, scheduleRetryCheck]);

  const clear = useCallback(() => {
    pendingRef.current = null;
    clearRetryTimer();
  }, [clearRetryTimer]);

  useEffect(() => clear, [clear]);

  return { seek, reconcile, clear };
}
